import { Icon, Pill } from '@iwoca/orion';
import clsx from 'clsx';
import { CopyableText } from 'components/CopyableText/CopyableText';
import { useReviewOffer } from 'components/ReviewOffer/ReviewOffer.context';
import { TColumnData } from 'components/SummaryCard/SummaryCard.helpers';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useGetAllowedWithdrawalFee } from 'lendingApi/lendingApiHooks';
import { useFormattedOfferDuration } from 'utils/durationHelper';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from '../../SummaryCard.module.css';
import { getOfferSelectionStatus } from '../SummaryCardColumn.helpers';

type TProps = {
  applicationId: string;
  offer: TColumnData & { offer_id: string };
  hasAction: boolean;
  clipboardText: string;
};

export const ColumnHeader = ({
  offer,
  hasAction,
  applicationId,
  clipboardText,
}: TProps) => {
  const { isUK } = useLocale();
  const { introducerId } = useIntroducerContext();
  const {
    selectedOfferId,
    selectedApplication,
    offerDecision,
    hasInterestOnlyOffer,
  } = useReviewOffer();
  const { trackEvent } = useAmplitude();
  const formattedDuration = useFormattedOfferDuration(offer.duration);

  const { offerIsSelected, offerNotSelected } = getOfferSelectionStatus(
    offerDecision,
    selectedOfferId,
    offer.offer_id,
  );

  const { data: allowedWithdrawalFees } = useGetAllowedWithdrawalFee(
    introducerId as string,
    applicationId,
    offer.offer_id,
    offer.product_type as string,
  );

  const isPartnerChannel = selectedApplication.is_partner_channel;

  const isPGNotRequired = offer.personal_guarantee_proportion === 0;

  return (
    <CopyableText
      className={clsx(styles.columnHeader, {
        [styles.selectedHeader]: offerIsSelected,
        [styles.unselectedValue]: offerNotSelected,
        [styles.noActionHeader]: !hasAction,
      })}
      text={clipboardText}
      disabled={isPartnerChannel || !hasAction}
      testId="copy-offer-product"
      onCopy={() => {
        trackEvent(AMPLITUDE_EVENTS.copyOfferClicked);
      }}
    >
      <div>
        {isPGNotRequired && isUK && (
          <Pill className={styles.noPGPill}>PG not required</Pill>
        )}
        <h2 data-testid="offer-product-type" className={styles.productType}>
          {formattedDuration}
        </h2>
        {allowedWithdrawalFees?.data?.interest_only_repayments &&
          hasInterestOnlyOffer && <span>First 3 months interest-only</span>}
      </div>
      {offerIsSelected && <Icon icon="checkDefault" />}
    </CopyableText>
  );
};
